// @ts-nocheck
import React, { useState, useEffect } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  Cell
} from 'recharts';
import {
  TrendingUp,
  TrendingDown,
  Users,
  Calendar,
  ChevronDown,
  ChevronRight,
  Sparkles,
  Download,
  Filter,
  Grid,
  List,
  ThumbsUp,
  AlertTriangle,
  MessageCircle,
  HeartHandshake,
  Settings,
  Crown,
  Shield,
  Globe,
  Target,
  Lightbulb,
  Clock,
  X,
  RefreshCw
} from 'lucide-react';
import type { ClimateTrends as ClimateTrendsType } from '@/types/performance';
import { getMyAllReports } from '@/api/workforce';
import type { Employee } from '@/types/workforce';

// 🚀 NEW: TanStack Query hooks for server state
import { useClimateTrends } from '@/hooks/queries'

// 🎨 NEW: Zustand UI store for client state
import { useClimateUIStore } from '@/stores/climate-ui-store'

const ClimaOrganizacional = () => {
  // 🎨 UI State (Zustand)
  const {
    activeTab,
    setActiveTab,
    viewMode,
    setViewMode,
    expandedCards,
    toggleCard,
    selectedPeriod,
    setSelectedPeriod,
    selectedDepartment,
    setSelectedDepartment,
  } = useClimateUIStore()

  // 🚀 Server State (TanStack Query)
  const {
    data: climateTrends,
    isLoading: loading,
    isFetching,
    error: queryError,
  } = useClimateTrends({
    period: selectedPeriod,
    departmentId: selectedDepartment,
  })

  const error = queryError ? 'Erro ao carregar tendências de clima.' : null

  // Local state for workforce employees
  const [realEmployees, setRealEmployees] = useState<Employee[]>([]);

  // Fetch employees for enhanced display (scoped to user's reports)
  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const response = await getMyAllReports({ limit: 100 });
        setRealEmployees(response.items);
      } catch (err) {
        console.error('Error fetching employees:', err);
        setRealEmployees([]);
      }
    };

    fetchEmployees();
  }, []);

  // ✅ No more manual useEffect for fetching climate trends - handled by TanStack Query!

  // Empty state detection logic
  const hasNoSurveys = !climateTrends || !climateTrends.surveys || climateTrends.surveys.length === 0;
  const belowPrivacyThreshold = climateTrends && climateTrends.metadata.totalResponses < 5;
  const hasFilteredData = climateTrends?.trendData && climateTrends.trendData.length > 0;
  const isFilteredEmpty = !hasNoSurveys && !hasFilteredData;

  // Process API data only when available
  const lastSurveyData = climateTrends?.surveys?.[0] ? {
    date: climateTrends.surveys[0].closedAt || new Date().toISOString(),
    respondents: climateTrends.metadata.totalResponses,
    totalEmployees: climateTrends.metadata.totalResponses,
    participationRate: 95.7,
    overallScore: climateTrends.trendData.reduce((sum, t) => sum + t.dataPoints[t.dataPoints.length - 1].value, 0) / climateTrends.trendData.length
  } : null;

  const heatmapData = climateTrends?.heatmapData.map(h => ({
    tema: h.category,
    score: Math.round(h.score * 20),
    intensity: h.intensity
  })) || [];

  const barChartData = climateTrends?.heatmapData.map(h => ({
    tema: h.category,
    atual: Math.round(h.score * 20),
    anterior: 0,
    meta: 80
  })) || [];

  const radarData = climateTrends?.heatmapData.map(h => ({
    subject: h.category,
    A: Math.round(h.score * 20),
    fullMark: 100
  })) || [];

  const timelineData = climateTrends?.trendData?.[0]?.dataPoints.map(dp => ({
    periodo: new Date(dp.timestamp).toLocaleDateString('pt-BR', { month: 'short', year: '2-digit' }),
    score: Math.round(dp.value * 20)
  })) || [];

  const historicalSurveys = climateTrends?.surveys.map((survey, idx) => ({
    periodo: new Date(survey.closedAt || survey.launchedAt).toLocaleDateString('pt-BR', { month: 'short', year: 'numeric' }),
    indiceGeral: survey.metadata?.averageScore || 0,
    comparativo: idx > 0 ? '+0.0' : '—',
    tendencia: 'stable' as const
  })) || [];

  const topForces = climateTrends?.summary.improving.slice(0, 3).map((category) => {
    const trendData = climateTrends.trendData.find(t => t.category === category);
    return {
      tema: category,
      score: Math.round((trendData?.dataPoints[trendData.dataPoints.length - 1].value || 0) * 20),
      trend: 'up' as const
    };
  }) || [];

  const attentionPoints = climateTrends?.summary.declining.slice(0, 3).map((category) => {
    const trendData = climateTrends.trendData.find(t => t.category === category);
    return {
      tema: category,
      score: Math.round((trendData?.dataPoints[trendData.dataPoints.length - 1].value || 0) * 20),
      trend: 'down' as const
    };
  }) || [];

  // Dados para o quadro interativo
  const climaThemes = [
    {
      id: 'comunicacao',
      title: 'Comunicação',
      description: 'Clareza, transparência, feedbacks construtivos',
      icon: MessageCircle,
      color: 'blue',
      question: 'Como você avalia a clareza e transparência da comunicação na empresa?',
      currentScore: 72,
      comments: 'A comunicação melhorou com os novos canais, mas ainda há ruído entre áreas.',
      insights: 'IA detectou que 68% mencionam melhorias nos últimos 3 meses.'
    },
    {
      id: 'colaboracao',
      title: 'Colaboração',
      description: 'Cooperação, espírito de equipe, suporte',
      icon: HeartHandshake,
      color: 'green',
      question: 'Como está o nível de colaboração e suporte entre as equipes?',
      currentScore: 85,
      comments: 'Excelente cooperação, especialmente em projetos cross-funcionais.',
      insights: 'IA identificou que projetos colaborativos têm 23% mais sucesso.'
    },
    {
      id: 'processos',
      title: 'Processos e Ferramentas',
      description: 'Eficiência, organização, uso adequado do Wrike e sistemas',
      icon: Settings,
      color: 'orange',
      question: 'Os processos e ferramentas (como Wrike) facilitam seu trabalho?',
      currentScore: 68,
      comments: 'Wrike é útil, mas alguns processos ainda são complexos.',
      insights: 'IA sugere treinamentos específicos para otimizar o uso das ferramentas.'
    },
    {
      id: 'lideranca',
      title: 'Liderança',
      description: 'Suporte, reconhecimento, desenvolvimento das pessoas',
      icon: Crown,
      color: 'purple',
      question: 'Como você avalia o suporte e reconhecimento da liderança?',
      currentScore: 91,
      comments: 'Líderes são muito acessíveis e apoiam o desenvolvimento profissional.',
      insights: 'IA detectou correlação positiva entre liderança e retenção de talentos.'
    },
    {
      id: 'bemestar',
      title: 'Bem-estar',
      description: 'Saúde física/psicológica, equilíbrio vida-trabalho, Wellhub/Zenklub',
      icon: Shield,
      color: 'red',
      question: 'Como está seu bem-estar e equilíbrio entre vida pessoal e trabalho?',
      currentScore: 58,
      comments: 'Pressão alta em picos de projeto. Benefícios como Wellhub ajudam.',
      insights: 'IA recomenda ações específicas para melhorar work-life balance.'
    },
    {
      id: 'diversidade',
      title: 'Respeito à Diversidade',
      description: 'Inclusão, equidade, valorização das diferenças',
      icon: Globe,
      color: 'teal',
      question: 'A empresa promove um ambiente inclusivo e diverso?',
      currentScore: 88,
      comments: 'Ambiente muito inclusivo, com iniciativas constantes de D&I.',
      insights: 'IA mostra que diversidade impacta positivamente na inovação.'
    },
    {
      id: 'satisfacao',
      title: 'Satisfação Geral',
      description: 'Motivação, engajamento, retenção',
      icon: ThumbsUp,
      color: 'yellow',
      question: 'Qual seu nível geral de satisfação em trabalhar na Monumenta?',
      currentScore: 75,
      comments: 'Gosto muito de trabalhar aqui, mas há espaço para melhorias.',
      insights: 'IA identifica que satisfação correlaciona com crescimento profissional.'
    },
    {
      id: 'alinhamento',
      title: 'Alinhamento com Princípios Monu',
      description: 'Criatividade, responsabilidade, ética, cuidado, aprendizado contínuo',
      icon: Target,
      color: 'indigo',
      question: 'Você sente alinhamento com os valores e princípios da Monumenta?',
      currentScore: 92,
      comments: 'Total alinhamento com os valores. A cultura é muito forte.',
      insights: 'IA mostra que alinhamento cultural é o maior diferencial da empresa.'
    }
  ];

  const [evaluations, setEvaluations] = useState(
    climaThemes.reduce((acc, theme) => ({
      ...acc,
      [theme.id]: {
        score: theme.currentScore,
        comment: theme.comments
      }
    }), {})
  );

  // toggleCard is now from Zustand store

  const updateEvaluation = (themeId, field, value) => {
    setEvaluations(prev => ({
      ...prev,
      [themeId]: {
        ...prev[themeId],
        [field]: value
      }
    }));
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-success';
    if (score >= 70) return 'text-warning';
    return 'text-error';
  };

  const getIntensityColor = (intensity) => {
    switch (intensity) {
      case 'high': return 'bg-success';
      case 'medium': return 'bg-warning';
      case 'low': return 'bg-error';
      default: return 'bg-brand-gray';
    }
  };

  // Empty State Components
  const SurveyNotLaunchedState = () => (
    <div className="flex flex-col items-center justify-center text-center p-16 space-y-6 bg-brand-dark border border-brand-gray/10 rounded-monumenta">
      <div className="relative">
        <TrendingUp className="h-16 w-16 text-brand-primary animate-pulse" />
        <Sparkles className="h-8 w-8 text-brand-primary absolute -top-2 -right-2" />
      </div>

      <div className="space-y-3 max-w-2xl">
        <h3 className="text-2xl font-display font-bold text-white">
          Pesquisa de clima em breve
        </h3>
        <p className="text-brand-gray leading-relaxed">
          A pesquisa de clima organizacional é lançada pelo RH e avalia 8 dimensões do bem-estar da equipe:
          comunicação, colaboração, processos, liderança, bem-estar, diversidade, satisfação e alinhamento cultural.
          Você será notificado quando a próxima pesquisa estiver disponível.
        </p>
      </div>

      <div className="flex flex-col sm:flex-row gap-4 pt-4">
        <button className="btn-primary flex items-center gap-2">
          <Clock className="h-4 w-4" />
          Ativar Notificações
        </button>
        <button className="btn-secondary">
          Ver Exemplo de Pesquisa
        </button>
        <button className="btn-secondary">
          Entender os 8 Temas
        </button>
      </div>
    </div>
  );

  const PrivacyThresholdBanner = ({ respondentCount }: { respondentCount: number }) => (
    <div className="bg-warning/10 border border-warning/30 rounded-monumenta p-6 mb-6">
      <div className="flex items-start gap-4">
        <div className="flex items-center gap-2 flex-shrink-0">
          <Shield className="h-5 w-5 text-warning" />
          <Users className="h-5 w-5 text-warning" />
        </div>
        <div className="flex-1 space-y-3">
          <h3 className="text-lg font-display font-bold text-warning">
            Resultados protegidos por privacidade
          </h3>
          <p className="text-brand-gray">
            Para garantir o anonimato, os resultados só são exibidos quando ao menos 5 pessoas respondem.
            Atualmente: <span className="font-bold text-warning">{respondentCount}/5 respostas</span>.
            Incentive mais pessoas a participarem!
          </p>
          <button className="btn-primary bg-warning hover:bg-warning/80 text-brand-dark">
            Compartilhar Convite
          </button>
        </div>
      </div>
    </div>
  );

  const FilteredEmptyState = () => (
    <div className="flex flex-col items-center justify-center text-center p-12 space-y-6 bg-brand-dark border border-brand-gray/10 rounded-monumenta">
      <div className="relative">
        <Filter className="h-12 w-12 text-brand-gray/50" />
        <X className="h-6 w-6 text-brand-gray/50 absolute -top-1 -right-1" />
      </div>

      <div className="space-y-2 max-w-xl">
        <h3 className="text-xl font-display font-bold text-white">
          Nenhum resultado para este filtro
        </h3>
        <p className="text-brand-gray">
          Não há dados de clima para o período selecionado ({selectedPeriod} meses)
          {selectedDepartment && ` e departamento selecionado`}.
          Tente expandir o período ou remover filtros.
        </p>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 pt-2">
        <button
          onClick={() => {
            setSelectedPeriod(12);
            setSelectedDepartment(undefined);
          }}
          className="btn-primary"
        >
          Limpar Filtros
        </button>
        <button
          onClick={() => setSelectedPeriod(12)}
          className="btn-secondary"
        >
          Expandir para 12 meses
        </button>
      </div>
    </div>
  );

  const renderResultados = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center h-64">
          <div className="text-white font-display">Carregando tendências de clima...</div>
        </div>
      );
    }

    if (error && !climateTrends) {
      return (
        <div className="bg-warning/10 border border-warning/30 rounded-monumenta p-4">
          <div className="flex items-center space-x-3">
            <AlertTriangle className="text-warning" size={20} />
            <p className="text-sm text-brand-gray">{error}</p>
          </div>
        </div>
      );
    }

    // Empty state checks
    if (hasNoSurveys) {
      return <SurveyNotLaunchedState />;
    }

    if (belowPrivacyThreshold) {
      return <PrivacyThresholdBanner respondentCount={climateTrends?.metadata.totalResponses || 0} />;
    }

    if (isFilteredEmpty) {
      return <FilteredEmptyState />;
    }

    return (
    <div className="space-y-6">

      {/* Error Warning */}
      {error && (
        <div className="bg-warning/10 border border-warning/30 rounded-monumenta p-4 mb-6">
          <div className="flex items-center space-x-3">
            <AlertTriangle className="text-warning" size={20} />
            <p className="text-sm text-brand-gray flex-1">{error}</p>
          </div>
        </div>
      )}

      <div className="flex items-center gap-4 mb-6">
        <label className="text-white font-display font-medium">Período:</label>
        <select
          value={selectedPeriod.toString()}
          onChange={(e) => setSelectedPeriod(parseInt(e.target.value))}
          className="form-select bg-brand-dark-light border-brand-gray/20 text-white rounded-monumenta px-4 py-2 w-48 focus:outline-none focus:border-brand-primary transition-colors"
        >
          <option value="3">Últimos 3 meses</option>
          <option value="6">Últimos 6 meses</option>
          <option value="12">Últimos 12 meses</option>
          <option value="24">Últimos 24 meses</option>
        </select>
      </div>

      {/* Resumo Geral */}
      {lastSurveyData && (
        <div className="card">
          <h3 className="text-xl font-display font-semibold text-white mb-6">Resumo da Última Pesquisa</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="flex items-center justify-center gap-2 mb-2">
                <Calendar className="h-5 w-5 text-brand-blue" />
                <span className="text-brand-blue font-display font-medium">Data</span>
              </div>
              <p className="text-white text-lg font-display">{new Date(lastSurveyData.date).toLocaleDateString('pt-BR')}</p>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center gap-2 mb-2">
                <Users className="h-5 w-5 text-success" />
                <span className="text-success font-display font-medium">Respondentes</span>
              </div>
              <p className="text-white text-lg font-display">{lastSurveyData.respondents}/{lastSurveyData.totalEmployees}</p>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center gap-2 mb-2">
                <TrendingUp className="h-5 w-5 text-warning" />
                <span className="text-warning font-display font-medium">Participação</span>
              </div>
              <p className="text-white text-lg font-display">{lastSurveyData.participationRate}%</p>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center gap-2 mb-2">
                <Target className="h-5 w-5 text-brand-primary" />
                <span className="text-brand-primary font-display font-medium">Índice Geral</span>
              </div>
              <p className="text-white text-lg font-display">{lastSurveyData.overallScore}</p>
            </div>
          </div>
        </div>
      )}

      {/* Top 3 Forças e Pontos de Atenção */}
      {(topForces.length > 0 || attentionPoints.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {topForces.length > 0 && (
            <div className="card">
              <h3 className="text-xl font-display font-semibold text-white mb-6 flex items-center gap-2">
                <ThumbsUp className="h-5 w-5 text-success" />
                Top 3 Forças
              </h3>
              <div className="space-y-3">
                {topForces.map((item, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-success/10 rounded-monumenta border border-success/30">
                <div>
                  <p className="text-white font-display font-medium">{item.tema}</p>
                  <p className="text-success text-sm">Pontuação: {item.score}</p>
                </div>
                <div className="flex items-center gap-2">
                  {item.trend === 'up' && <TrendingUp className="h-4 w-4 text-success" />}
                  <span className="px-3 py-1 bg-success rounded-monumenta text-white font-display font-medium text-sm">{item.score}</span>
                </div>
              </div>
                ))}
              </div>
            </div>
          )}

          {attentionPoints.length > 0 && (
            <div className="card">
              <h3 className="text-xl font-display font-semibold text-white mb-6 flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-error" />
                Top 3 Pontos de Atenção
              </h3>
              <div className="space-y-3">
                {attentionPoints.map((item, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-error/10 rounded-monumenta border border-error/30">
                <div>
                  <p className="text-white font-display font-medium">{item.tema}</p>
                  <p className="text-error text-sm">Pontuação: {item.score}</p>
                </div>
                <div className="flex items-center gap-2">
                  {item.trend === 'down' && <TrendingDown className="h-4 w-4 text-error" />}
                  <span className="px-3 py-1 bg-error rounded-monumenta text-white font-display font-medium text-sm">{item.score}</span>
                </div>
              </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Mapa de Calor */}
      {heatmapData.length > 0 && (
        <div className="card">
          <h3 className="text-xl font-display font-semibold text-white mb-6">Mapa de Calor - Satisfação por Tema</h3>
          <div className="space-y-2">
            {heatmapData.map((item, index) => (
            <div key={index} className="flex items-center gap-4">
              <div className="w-40 text-white text-sm font-display">{item.tema}</div>
              <div className="flex-1 bg-brand-dark-light rounded-monumenta h-8 flex items-center relative">
                <div
                  className={`h-full rounded-monumenta ${getIntensityColor(item.intensity)} transition-all duration-500`}
                  style={{ width: `${item.score}%` }}
                />
                <span className="absolute right-3 text-white text-sm font-display font-medium">
                  {item.score}
                </span>
              </div>
            </div>
            ))}
          </div>
        </div>
      )}

      {/* Gráficos */}
      {(barChartData.length > 0 || radarData.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Gráfico de Barras Comparativas */}
          {barChartData.length > 0 && (
            <div className="card">
          <h3 className="text-xl font-display font-semibold text-white mb-6">Comparativo por Tema</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={barChartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#2A2D34" />
              <XAxis
                dataKey="tema"
                stroke="#838998"
                angle={-45}
                textAnchor="end"
                height={100}
              />
              <YAxis stroke="#838998" />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#24262B',
                  border: '1px solid #2A2D34',
                  borderRadius: '12px',
                  color: '#FFFFFF'
                }}
              />
              <Bar dataKey="anterior" fill="#838998" name="Pesquisa Anterior" />
              <Bar dataKey="atual" fill="#10B981" name="Pesquisa Atual" />
              <Bar dataKey="meta" fill="#F59E0B" name="Meta" />
            </BarChart>
          </ResponsiveContainer>
            </div>
          )}

          {/* Radar Chart */}
          {radarData.length > 0 && (
            <div className="card">
          <h3 className="text-xl font-display font-semibold text-white mb-6">Radar de Performance</h3>
          <ResponsiveContainer width="100%" height={300}>
            <RadarChart data={radarData}>
              <PolarGrid stroke="#2A2D34" />
              <PolarAngleAxis dataKey="subject" tick={{ fill: '#838998', fontSize: 12 }} />
              <PolarRadiusAxis
                domain={[0, 100]}
                tick={{ fill: '#838998', fontSize: 10 }}
              />
              <Radar
                name="Pontuação"
                dataKey="A"
                stroke="#10B981"
                fill="#10B981"
                fillOpacity={0.3}
                strokeWidth={2}
              />
            </RadarChart>
          </ResponsiveContainer>
            </div>
          )}
        </div>
      )}

      {/* Linha do Tempo */}
      {timelineData.length > 0 && (
        <div className="card">
        <h3 className="text-xl font-display font-semibold text-white mb-6">Evolução do Clima Organizacional</h3>
        <ResponsiveContainer width="100%" height={250}>
          <LineChart data={timelineData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#2A2D34" />
            <XAxis dataKey="periodo" stroke="#838998" />
            <YAxis stroke="#838998" domain={[60, 85]} />
            <Tooltip
              contentStyle={{
                backgroundColor: '#24262B',
                border: '1px solid #2A2D34',
                borderRadius: '12px',
                color: '#FFFFFF'
              }}
            />
            <Line
              type="monotone"
              dataKey="score"
              stroke="#10B981"
              strokeWidth={3}
              dot={{ fill: '#10B981', strokeWidth: 2, r: 6 }}
            />
          </LineChart>
        </ResponsiveContainer>
        </div>
      )}

      {/* Histórico de Pesquisas */}
      {historicalSurveys.length > 0 && (
        <div className="card">
        <h3 className="text-xl font-display font-semibold text-white mb-6">Histórico de Pesquisas</h3>
        <div className="space-y-3">
          {historicalSurveys.map((survey, index) => (
            <div key={index} className="flex items-center justify-between p-3 bg-brand-dark-light rounded-monumenta">
              <div className="flex items-center gap-3">
                <Calendar className="h-4 w-4 text-brand-gray" />
                <span className="text-white font-display font-medium">{survey.periodo}</span>
              </div>
              <div className="flex items-center gap-4">
                <span className="text-white font-display">{survey.indiceGeral}</span>
                <div className="flex items-center gap-1">
                  {survey.tendencia === 'up' ? (
                    <TrendingUp className="h-4 w-4 text-success" />
                  ) : (
                    <TrendingDown className="h-4 w-4 text-error" />
                  )}
                  <span className={survey.tendencia === 'up' ? 'text-success' : 'text-error'}>
                    {survey.comparativo}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
        </div>
      )}
    </div>
    );
  };

  const renderQuadroInterativo = () => (
    <div className="space-y-6">
      {/* Controles */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <select
            defaultValue="todas"
            className="form-select bg-brand-dark-light border-brand-gray/20 text-white rounded-monumenta px-4 py-2 w-48 focus:outline-none focus:border-brand-primary transition-colors"
          >
            <option value="todas">Todas as Equipes</option>
            <option value="tech">Tecnologia</option>
            <option value="design">Design</option>
            <option value="bi">BI & Analytics</option>
          </select>

          <div className="flex items-center gap-2 bg-brand-dark-light rounded-monumenta p-1">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-monumenta transition-colors ${viewMode === 'grid' ? 'bg-brand-primary text-brand-dark' : 'text-brand-gray hover:text-white'}`}
            >
              <Grid className="h-4 w-4" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-monumenta transition-colors ${viewMode === 'list' ? 'bg-brand-primary text-brand-dark' : 'text-brand-gray hover:text-white'}`}
            >
              <List className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button className="btn-primary flex items-center gap-2">
            <Sparkles className="h-4 w-4" />
            Gerar Insights com IA
          </button>
          <button className="btn-secondary">
            <Download className="h-4 w-4 mr-2" />
            Exportar
          </button>
          <button className="btn-secondary">
            <Filter className="h-4 w-4 mr-2" />
            Filtros
          </button>
        </div>
      </div>

      {/* Grid de Temas */}
      <div className={`grid gap-6 ${viewMode === 'grid' ? 'grid-cols-1 lg:grid-cols-2' : 'grid-cols-1'}`}>
        {climaThemes.map((theme) => {
          const IconComponent = theme.icon;
          const isExpanded = expandedCards[theme.id];

          return (
            <div key={theme.id} className="card-interactive">
              <div
                onClick={() => toggleCard(theme.id)}
                className="cursor-pointer"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 bg-brand-primary/10 rounded-monumenta flex items-center justify-center`}>
                      <IconComponent className="h-5 w-5 text-brand-primary" />
                    </div>
                    <div>
                      <h4 className="text-lg font-display font-semibold text-white">{theme.title}</h4>
                      <p className="text-brand-gray text-sm">{theme.description}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <p className={`text-xl font-bold font-display ${getScoreColor(evaluations[theme.id]?.score || theme.currentScore)}`}>
                        {evaluations[theme.id]?.score || theme.currentScore}
                      </p>
                      <p className="text-brand-gray text-xs">Pontuação</p>
                    </div>
                    {isExpanded ? (
                      <ChevronDown className="h-5 w-5 text-brand-gray" />
                    ) : (
                      <ChevronRight className="h-5 w-5 text-brand-gray" />
                    )}
                  </div>
                </div>
              </div>

              {isExpanded && (
                <div className="space-y-6 mt-6 pt-6 border-t border-brand-gray/20">
                  {/* Pergunta Chave */}
                  <div>
                    <h4 className="text-white font-display font-medium mb-2">Pergunta-chave:</h4>
                    <p className="text-brand-gray text-sm bg-brand-dark-light p-3 rounded-monumenta">{theme.question}</p>
                  </div>

                  {/* Escala de Avaliação */}
                  <div>
                    <h4 className="text-white font-display font-medium mb-3">Avaliação (0-100):</h4>
                    <div className="space-y-3">
                      <input
                        type="range"
                        value={evaluations[theme.id]?.score || theme.currentScore}
                        onChange={(e) => updateEvaluation(theme.id, 'score', parseInt(e.target.value))}
                        max={100}
                        step={1}
                        className="w-full"
                      />
                      <div className="flex justify-between text-brand-gray text-sm">
                        <span>0 - Muito Insatisfeito</span>
                        <span className={`font-medium font-display ${getScoreColor(evaluations[theme.id]?.score || theme.currentScore)}`}>
                          {evaluations[theme.id]?.score || theme.currentScore}
                        </span>
                        <span>100 - Muito Satisfeito</span>
                      </div>
                    </div>
                  </div>

                  {/* Comentário */}
                  <div>
                    <h4 className="text-white font-display font-medium mb-2">Comentário qualitativo:</h4>
                    <textarea
                      value={evaluations[theme.id]?.comment || theme.comments}
                      onChange={(e) => updateEvaluation(theme.id, 'comment', e.target.value)}
                      className="form-input w-full bg-brand-dark-light border-brand-gray/20 text-white rounded-monumenta min-h-20 p-3 focus:outline-none focus:border-brand-primary transition-colors"
                      placeholder="Compartilhe sua experiência, sugestões ou observações..."
                    />
                  </div>

                  {/* Insights de IA */}
                  <div className="bg-brand-primary/10 border border-brand-primary/30 rounded-monumenta p-4">
                    <div className="flex items-start space-x-3">
                      <Lightbulb className="h-5 w-5 text-brand-primary flex-shrink-0 mt-0.5" />
                      <div>
                        <strong className="text-brand-primary font-display">Insight de IA:</strong>
                        <p className="text-brand-gray text-sm mt-1">{theme.insights}</p>
                      </div>
                    </div>
                  </div>

                  {/* Indicadores Visuais */}
                  <div className="flex items-center justify-between p-3 bg-brand-dark-light rounded-monumenta">
                    <span className="text-brand-gray font-display">Status do tema:</span>
                    <div className="flex items-center gap-2">
                      {(evaluations[theme.id]?.score || theme.currentScore) >= 80 && (
                        <span className="px-3 py-1 bg-success rounded-monumenta text-white font-display font-medium text-sm">Excelente</span>
                      )}
                      {(evaluations[theme.id]?.score || theme.currentScore) >= 70 &&
                       (evaluations[theme.id]?.score || theme.currentScore) < 80 && (
                        <span className="px-3 py-1 bg-warning rounded-monumenta text-white font-display font-medium text-sm">Bom</span>
                      )}
                      {(evaluations[theme.id]?.score || theme.currentScore) < 70 && (
                        <span className="px-3 py-1 bg-error rounded-monumenta text-white font-display font-medium text-sm">Atenção</span>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Ações */}
      <div className="flex justify-center gap-4 pt-6">
        <button className="btn-primary">
          Salvar Avaliações
        </button>
        <button className="btn-secondary">
          Salvar como Rascunho
        </button>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-white text-2xl font-display font-bold">Clima Organizacional</h2>
          <p className="text-brand-gray">Monitoramento e avaliação do bem-estar organizacional</p>
        </div>

        <div className="bg-brand-primary px-4 py-2 rounded-monumenta">
          <span className="text-brand-dark font-display font-medium">Última pesquisa: Nov/24</span>
        </div>
      </div>

      {/* Custom Tabs Implementation */}
      <div className="space-y-6">
        <div className="flex gap-2 bg-brand-dark-light p-1 rounded-monumenta">
          <button
            onClick={() => setActiveTab('resultados')}
            className={`flex-1 px-6 py-3 rounded-monumenta font-display font-medium transition-all ${
              activeTab === 'resultados'
                ? 'bg-brand-primary text-brand-dark'
                : 'text-brand-gray hover:text-white'
            }`}
          >
            Resultados
          </button>
          <button
            onClick={() => setActiveTab('quadro')}
            className={`flex-1 px-6 py-3 rounded-monumenta font-display font-medium transition-all ${
              activeTab === 'quadro'
                ? 'bg-brand-primary text-brand-dark'
                : 'text-brand-gray hover:text-white'
            }`}
          >
            Quadro Interativo
          </button>
        </div>

        <div className="mt-6">
          {activeTab === 'resultados' && renderResultados()}
          {activeTab === 'quadro' && renderQuadroInterativo()}
        </div>
      </div>
    </div>
  );
};

export default ClimaOrganizacional;
